import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, ArrowRight, Zap } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { CONTACT, ABOUT, NAV_LINKS } from '../mock';
import Reveal from './Reveal';
import SiteFooter from './SiteFooter';

export default function ContactSection() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    const list = JSON.parse(localStorage.getItem('newsletter') || '[]');
    list.push({ email, at: Date.now() });
    localStorage.setItem('newsletter', JSON.stringify(list));
    toast({ title: 'Subscribed!', description: 'You are on the list for the latest insights.' });
    setEmail('');
  };

  const submit = (e) => {
    e.preventDefault();
    const list = JSON.parse(localStorage.getItem('messages') || '[]');
    list.push({ ...form, at: Date.now() });
    localStorage.setItem('messages', JSON.stringify(list));
    toast({ title: 'Message sent!', description: "Thanks \u2014 we'll be in touch shortly." });
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <>
      {/* NEWSLETTER */}
      <section className="relative py-20 px-5">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="relative neon-border rounded-3xl p-10 md:p-14 overflow-hidden">
              <div className="blob bg-[#0a6bd6] w-72 h-72 -top-20 -right-10 opacity-30" />
              <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-display text-3xl md:text-4xl font-bold">Stay Updated</h2>
                  <p className="text-white/55 mt-3">Subscribe for the latest insights, launches and field notes.</p>
                </div>
                <form onSubmit={subscribe} className="flex gap-3">
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="bg-white/5 border-white/15 h-12 text-white placeholder:text-white/40"
                  />
                  <button type="submit" className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0ef3fe] to-[#0a6bd6] text-black px-6 font-semibold hover:shadow-[0_0_25px_rgba(14,243,254,0.5)] transition-shadow">
                    Subscribe <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT + CONTACT */}
      <section id="contact" className="relative py-24 px-5 grid-bg">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
          <Reveal>
            <div>
              <span className="font-mono-accent text-xs text-[#0ef3fe]">// ABOUT US</span>
              <h2 className="font-display text-4xl font-bold mt-3 mb-5">About Electronico Holdings</h2>
              <p className="text-white/60 leading-relaxed mb-8">{ABOUT}</p>

              <div className="space-y-4">
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 neon-border rounded-2xl p-4 hover:border-[#0ef3fe]/50 transition-colors group">
                  <span className="h-11 w-11 rounded-xl bg-[#0ef3fe]/10 flex items-center justify-center"><Mail className="h-5 w-5 text-[#0ef3fe]" /></span>
                  <div><div className="text-xs text-white/40">Email</div><div className="font-medium group-hover:text-[#0ef3fe] transition-colors">{CONTACT.email}</div></div>
                </a>
                <a href={`tel:${CONTACT.phone.replace(/\s/g, '')}`} className="flex items-center gap-4 neon-border rounded-2xl p-4 hover:border-[#088ffe]/50 transition-colors group">
                  <span className="h-11 w-11 rounded-xl bg-[#088ffe]/10 flex items-center justify-center"><Phone className="h-5 w-5 text-[#088ffe]" /></span>
                  <div><div className="text-xs text-white/40">Phone</div><div className="font-medium group-hover:text-[#088ffe] transition-colors">{CONTACT.phone}</div></div>
                </a>
                <div className="flex items-center gap-4 neon-border rounded-2xl p-4">
                  <span className="h-11 w-11 rounded-xl bg-[#5bd0ff]/10 flex items-center justify-center"><MapPin className="h-5 w-5 text-[#5bd0ff]" /></span>
                  <div><div className="text-xs text-white/40">Location</div><div className="font-medium">{CONTACT.location}</div></div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form onSubmit={submit} className="neon-border rounded-3xl p-8 space-y-4">
              <h3 className="font-display text-2xl font-bold mb-2">Get In Touch</h3>
              <p className="text-sm text-white/50 mb-4">Ready to start your next project? Contact us today.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="bg-white/5 border-white/15 h-12 text-white placeholder:text-white/40" />
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-white/5 border-white/15 h-12 text-white placeholder:text-white/40" />
              </div>
              <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" className="bg-white/5 border-white/15 h-12 text-white placeholder:text-white/40" />
              <Textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message" className="bg-white/5 border-white/15 text-white placeholder:text-white/40" />
              <button type="submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0ef3fe] to-[#088ffe] text-black py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(8,143,254,0.4)] transition-shadow">
                Send Message <Send className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </>
  );
}
